'use client';

import { QuestNavigationMenu } from '@diritto/common/navigation/constant';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { QuestNavigationBar } from 'app-components/common/navigations/QuestNavigationBar';
import { AllRankingList } from 'app-components/ranking/activityPoint/AllRankingList';
import { QuestRankingRewardInfo } from 'app-components/ranking/activityPoint/QuestRankingRewardInfo';
import { RankingMyInfo } from 'app-components/ranking/activityPoint/RankingMyInfo';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useGetActivityPointAllRankQuery } from 'queries/activityPoint/useGetActivityPointAllRankQuery';
import { useGetActivityPointMyRankQuery } from 'queries/activityPoint/useGetActivityPointMyRankQuery';
import { usePostRelayProfilesLiteQuery } from 'queries/relay/usePostRelayProfilesLiteQuery';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useActivityPointSeason } from 'state-hooks/activityPoint/useActivityPointSeason';
import { Divider } from 'ui-components/common/Divider';
import { EmptyList } from 'ui-components/common/EmptyList';
import { Loading } from 'ui-components/common/Loading';
import { StyledTopMenu } from 'ui-components/navigations/styles';

export const QuestRankingPage = () => {
  const { tracker } = useTrackingState();

  useEffect(() => {
    tracker.track(TrackingEventType.ViewQuestAll);
  }, [tracker]);

  const { isLoaded: isAuthLoaded, profile, isLoggedIn } = useAuthState();
  const { currentSeason, isPending: isCurrentSeasonPending } = useActivityPointSeason();
  const { data: activityPointMyRankData } = useGetActivityPointMyRankQuery(isLoggedIn);
  const { data: activityPointAllRankData, isPending: isActivityPointAllRankDataLoading } =
    useGetActivityPointAllRankQuery();

  const myRank = useMemo(
    () => profile && activityPointMyRankData?.rank.find(rank => rank.profileId === profile.id),
    [activityPointMyRankData, profile],
  );

  const profileIds = useMemo(
    () => [
      ...(activityPointMyRankData?.rank.map(rank => rank.profileId) ?? []),
      ...(activityPointAllRankData?.rank.map(rank => rank.profileId) ?? []),
    ],
    [activityPointAllRankData?.rank, activityPointMyRankData?.rank],
  );
  const { data: profileRelayData, isPending: isProfileRelayDataPending } = usePostRelayProfilesLiteQuery({
    profileIds,
  });

  const isLoading =
    !isAuthLoaded ||
    isCurrentSeasonPending ||
    (profileIds.length > 0 && isProfileRelayDataPending) ||
    isActivityPointAllRankDataLoading;

  const renderBody = useCallback(() => {
    if (isLoading) {
      return null;
    }

    if (!currentSeason || !activityPointAllRankData) {
      return <EmptyList title="시즌이 시작되면 공개돼요." description="조금만 기다려 주세요." />;
    }

    return (
      <>
        <QuestRankingRewardInfo season={currentSeason} />
        <AllRankingList
          myRank={myRank}
          activityPointAllRank={activityPointAllRankData.rank}
          profileLites={profileRelayData?.profileLites ?? {}}
        />
        {activityPointMyRankData && (
          <RankingMyInfo
            activityPointMyRank={activityPointMyRankData.rank}
            profileLites={profileRelayData?.profileLites ?? {}}
          />
        )}
      </>
    );
  }, [
    activityPointAllRankData,
    activityPointMyRankData,
    currentSeason,
    isLoading,
    myRank,
    profileRelayData?.profileLites,
  ]);

  return (
    <>
      <StyledTopMenu>
        <QuestNavigationBar selectedMenu={QuestNavigationMenu.Ranking} />
        <Divider />
      </StyledTopMenu>
      {renderBody()}
    </>
  );
};

export const QuestRankingPageSuspense = () => (
  <>
    <StyledTopMenu>
      <QuestNavigationBar selectedMenu={QuestNavigationMenu.Ranking} />
      <Divider />
    </StyledTopMenu>
    <Loading className="flex h-[50vh]" />
  </>
);
